import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@repletoryreach/sdk-auth';
import { identity } from '@repletoryreach/sdk-api';
import { Card, Stack, Group, Title, Text, Button, Badge } from '@repletoryreach/sdk-ui';
import { Alert, Stepper } from '@repletoryreach/sdk-ui/data-display';
import { TextInput, Select } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../i18n/I18nProvider';
import { usePersistedState } from '../../lib/usePersistedState';
import { ConnectWhatsApp } from '../numbers/ConnectWhatsApp';
import { AgreementForm, AGREEMENTS_KEY } from '../../app/components/AgreementForm';
import { formatDate } from '../../lib/format';
import './onboarding.css';
import { useIsOrgAdmin } from '../../app/components/RoleGate';

const STEP_KEYS = [
  'onboarding.steps.verify.label',
  'onboarding.steps.region.label',
  'onboarding.steps.agreement.label',
  'onboarding.steps.name.label',
  'onboarding.steps.number.label',
  'onboarding.steps.done.label'
];

const COUNTRIES = [
  { value: 'ZA', labelKey: 'country.za' },
  { value: 'NG', labelKey: 'country.ng' },
  { value: 'KE', labelKey: 'country.ke' }
];

/** First-run guided setup. Each step uses a real endpoint; only the data processing agreement cannot be skipped. */
export default function OnboardingPage() {
  const t = useT();
  const { user, verifyEmail, resendVerification, refresh } = useAuth();
  const navigate = useNavigate();

  /*
   * §4 item 1, "the single biggest defect on the page": `step` was
   * `useState(0)`, so a refresh, a dropped session, or following the wizard's
   * own "Open Numbers" link restarted it at step 0 and re-asked for a
   * verification code the user may already have spent. The wizard was not
   * resumable in any sense.
   */
  const [step, setStep] = usePersistedState('tenant.onboarding.step', 0);
  const [busy, setBusy] = useState(false);
  // The data processing agreement (2 October 2026), the one step that is required.
  const { data: agreements } = useQuery({
    queryKey: AGREEMENTS_KEY,
    queryFn: () => identity.listAgreements(),
    select: (r) => r.data
  });
  const dpa = agreements?.find((a) => a.kind === 'dpa');
  const [resending, setResending] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);

  const [code, setCode] = useState('');
  const [country, setCountry] = useState(user?.org.country_iso ?? 'ZA');
  const [displayName, setDisplayName] = useState(user?.display_name ?? '');
  // The business's name, asked with the person's (9 October 2026): a Google sign-up names the business after the
  // person, and a password sign-up sees the name it typed, to confirm or correct.
  const [businessName, setBusinessName] = useState(user?.org.name ?? '');
  // Only an owner or admin may rename the business; an invited agent is not asked.
  const canNameBusiness = useIsOrgAdmin();

  // §4 item 16: advancing left focus on a button that had just unmounted, so
  // keyboard users landed back at the document root.
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => { headingRef.current?.focus(); }, [step]);

  useEffect(() => {
    if (!user) return;
    setCountry(user.org.country_iso ?? 'ZA');
    // Signup seeds `display_name` with the email, which is not a name.
    setDisplayName(user.display_name && !user.display_name.includes('@') ? user.display_name : '');
    setBusinessName(user.org.name ?? '');
  }, [user?.org.country_iso, user?.display_name, user?.org.name]); // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * §4 item 10: an already-verified user was asked for a code that could only
   * fail. Skipping forward is done once, on arrival, so someone who lands here
   * deliberately is not bounced past a step they wanted to see.
   */
  const skippedVerify = useRef(false);
  useEffect(() => {
    if (skippedVerify.current || !user) return;
    skippedVerify.current = true;
    if (user.email_verified && step === 0) setStep(1);
  }, [user, step, setStep]);

  function finish() {
    try {
      window.localStorage.setItem('tenant.onboarding.completed', 'true');
      window.localStorage.removeItem('tenant.onboarding.step');
    } catch { /* ignore */ }
    navigate('/', { replace: true });
  }

  function go(next: number) {
    setStepError(null);
    setStep(next);
  }

  async function doVerify() {
    setBusy(true);
    setStepError(null);
    try {
      await verifyEmail(user?.email ?? '', code.trim());
      notifySuccess(t('onboarding.toast.emailVerified'), t('onboarding.toast.emailVerifiedTitle'));
      go(1);
    } catch (e) {
      // §4 item 6: a failed step left the user on the same screen with only a
      // toast, which is gone by the time they look for it.
      setStepError(e instanceof Error ? e.message : t('onboarding.toast.codeMismatch'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * §4 item 2: this was a floating promise with no await, no loading state and
   * no catch, so success showed nothing (inviting a second click) and failure
   * produced an unhandled rejection and, again, nothing on screen.
   */
  async function resend() {
    setResending(true);
    try {
      await resendVerification(user?.email ?? '');
      notifySuccess(t('onboarding.toast.codeResent', { email: user?.email ?? '' }), t('onboarding.steps.verify.title'));
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('onboarding.steps.verify.title'));
    } finally {
      setResending(false);
    }
  }

  async function doRegion() {
    setBusy(true);
    setStepError(null);
    try {
      await identity.updateOrganisation({ country_iso: country });
      await refresh();
      go(2);
    } catch (e) {
      setStepError(e instanceof Error ? e.message : t('onboarding.toast.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function doName() {
    setBusy(true);
    setStepError(null);
    try {
      if (displayName.trim()) await identity.updateProfile({ display_name: displayName.trim() });
      if (canNameBusiness && businessName.trim() && businessName.trim() !== user?.org.name) await identity.updateOrganisation({ name: businessName.trim() });
      await refresh();
      go(4);
    } catch (e) {
      setStepError(e instanceof Error ? e.message : t('onboarding.toast.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  /** §4 item 5: `setStep` only ever incremented, so a wrong country at step 1 could not be corrected. */
  const back = step > 0 ? () => go(step - 1) : null;

  return (
    <div className="rr-auth">
      <div className="rr-auth__brand">
        <h1>{t('onboarding.title')}</h1>
        <p>{t('onboarding.description')}</p>
      </div>
      <div className="rr-auth__panel">
        <Card withBorder className="rr-auth__card">
          <Stack gap="md">
            {/*
              §4 items 12 and 13: five bare divs with inline colours, no
              `role="progressbar"`, no per-step labels, and `i <= step` filled
              the CURRENT step as though it were finished.
            */}
            <div className="rr-onboarding__steps">
              <Stepper active={step} size="xs">
                {STEP_KEYS.map((k) => <Stepper.Step key={k} label={t(k)} />)}
              </Stepper>
            </div>
            <Text role="caption" c="var(--rr-color-text-muted)">
              {t('onboarding.stepIndicator', {
                current: step + 1,
                total: STEP_KEYS.length,
                label: t(STEP_KEYS[step])
              })}
            </Text>

            {stepError && <Alert color="error" role="alert">{stepError}</Alert>}

            {step === 0 && (
              <Stack gap="sm">
                <Title order={4} ref={headingRef} tabIndex={-1}>{t('onboarding.steps.verify.title')}</Title>
                <Text role="bodySmall" c="var(--rr-color-text-muted)">
                  {t('onboarding.steps.verify.sentTo', { email: user?.email ?? '' })}
                </Text>
                <TextInput
                  label={t('onboarding.steps.verify.form.code')}
                  value={code}
                  onChange={(e) => { setCode(e.currentTarget.value); setStepError(null); }}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                />
                <Group justify="space-between" wrap="wrap" gap="xs">
                  <Button size="xs" variant="subtle" loading={resending} onClick={resend}>
                    {t('onboarding.steps.verify.resend')}
                  </Button>
                  <Group gap="xs">
                    <Button size="xs" variant="default" onClick={() => go(1)}>
                      {t('onboarding.skipStep')}
                    </Button>
                    <Button size="xs" loading={busy} onClick={doVerify} disabled={!code.trim()}>
                      {t('onboarding.steps.verify.confirm')}
                    </Button>
                  </Group>
                </Group>
              </Stack>
            )}

            {step === 1 && (
              <Stack gap="sm">
                <Title order={4} ref={headingRef} tabIndex={-1}>{t('onboarding.steps.region.title')}</Title>
                <Text role="bodySmall" c="var(--rr-color-text-muted)">
                  {t('onboarding.steps.region.description')}
                </Text>
                <Select
                  label={t('onboarding.steps.region.form.country')}
                  value={country}
                  onChange={(e) => setCountry(e.currentTarget.value)}
                  data={COUNTRIES.map((c) => ({ value: c.value, label: t(c.labelKey) }))}
                />
                <StepFooter
                  back={back}
                  onSkip={() => go(2)}
                  onNext={doRegion}
                  busy={busy}
                  t={t}
                />
              </Stack>
            )}

            {step === 2 && (
              <Stack gap="sm">
                <Title order={4} ref={headingRef} tabIndex={-1}>{t('onboarding.steps.agreement.title')}</Title>
                {/* Required (owner's decision, 2 October 2026): the console is
                    not usable without it, so there is no Skip here. */}
                {dpa && dpa.accepted ? (
                  <>
                    <Text role="bodySmall">
                      {t('onboarding.steps.agreement.done', { date: formatDate(dpa.accepted_at ?? ''), who: dpa.accepted_by_email ?? '' })}
                    </Text>
                    <StepFooter back={back} onNext={() => go(3)} busy={false} t={t} />
                  </>
                ) : dpa ? (
                  <>
                    <AgreementForm agreement={dpa} onAccepted={() => go(3)} />
                    {back && <Group><Button size="xs" variant="subtle" onClick={back}>{t('action.back')}</Button></Group>}
                  </>
                ) : (
                  <StepFooter back={back} onNext={() => go(3)} busy={false} t={t} />
                )}
              </Stack>
            )}

            {step === 3 && (
              <Stack gap="sm">
                <Title order={4} ref={headingRef} tabIndex={-1}>{t('onboarding.steps.name.title')}</Title>
                <TextInput
                  label={t('onboarding.steps.name.form.displayName')}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.currentTarget.value)}
                  placeholder={t('onboarding.steps.name.form.displayNamePlaceholder')}
                />
                {canNameBusiness && <TextInput
                  label={t('onboarding.steps.name.form.businessName')}
                  description={t('onboarding.steps.name.form.businessNameHint')}
                  value={businessName}
                  onChange={(e) => setBusinessName(e.currentTarget.value)}
                  placeholder={t('onboarding.steps.name.form.businessNamePlaceholder')}
                  data-testid="onboarding-business-name"
                />}
                <StepFooter
                  back={back}
                  onSkip={() => go(4)}
                  onNext={doName}
                  busy={busy}
                  t={t}
                />
              </Stack>
            )}

            {step === 4 && (
              <Stack gap="sm">
                <Title order={4} ref={headingRef} tabIndex={-1}>{t('onboarding.steps.number.title')}</Title>
                <Text role="bodySmall" c="var(--rr-color-text-muted)">
                  {t('onboarding.steps.number.description')}
                </Text>
                {/*
                  W22: the step used to be a notice saying self-service
                  connection would arrive one day, and a link out to a page
                  asking for a "Meta phone-number-id". It is now the connection
                  itself, done here, without leaving the wizard.
                */}
                <ConnectWhatsApp size="md" onConnected={() => go(5)} />
                {/*
                  §4 item 9: "Open Numbers" navigated out of the wizard with no
                  return path and no saved progress, so whoever followed it
                  never came back. The step now persists, and the link says it
                  will keep the place.
                */}
                <Text role="caption" c="var(--rr-color-text-muted)">
                  {t('onboarding.steps.number.returnHint')}
                </Text>
                <StepFooter
                  back={back}
                  onSkip={() => go(5)}
                  onNext={() => go(5)}
                  busy={false}
                  t={t}
                  extra={(
                    <Link to="/numbers">
                      <Button size="xs" variant="default">{t('onboarding.steps.number.openNumbers')}</Button>
                    </Link>
                  )}
                />
              </Stack>
            )}

            {step === 5 && (
              <Stack gap="sm">
                <Group gap="xs" align="center">
                  <Title order={4} ref={headingRef} tabIndex={-1}>{t('onboarding.steps.done.title')}</Title>
                  <Badge size="xs" color="green" variant="light">{t('onboarding.steps.done.label')}</Badge>
                </Group>
                <Text role="bodySmall" c="var(--rr-color-text-muted)">
                  {t('onboarding.steps.done.description')}
                </Text>
                <Group justify="space-between">
                  {back && <Button size="xs" variant="subtle" onClick={back}>{t('action.back')}</Button>}
                  <Button onClick={finish}>{t('onboarding.steps.done.goToDashboard')}</Button>
                </Group>
              </Stack>
            )}

            {step < 4 && (
              <Group justify="center">
                <Button size="xs" variant="subtle" onClick={finish}>{t('onboarding.skipSetup')}</Button>
              </Group>
            )}
          </Stack>
        </Card>
      </div>
    </div>
  );
}

/**
 * The same three choices on every step: go back, skip this one, carry on.
 *
 * §4 item 7: the mental model changed on every screen. Step 0 had "Skip for
 * now" AND "Skip setup"; steps 1 and 2 had only the global exit, so a user who
 * could not complete a step had to abandon the whole wizard; step 3 had
 * neither.
 */
function StepFooter({
  back,
  onSkip,
  onNext,
  busy,
  t,
  extra
}: {
  back: (() => void) | null;
  /** Absent on a step that cannot be skipped. */
  onSkip?: () => void;
  onNext: () => void;
  busy: boolean;
  t: (k: string) => string;
  extra?: React.ReactNode;
}) {
  return (
    <Group justify="space-between" wrap="wrap" gap="xs">
      <Group gap="xs">
        {back && <Button size="xs" variant="subtle" onClick={back}>{t('action.back')}</Button>}
        {extra}
      </Group>
      <Group gap="xs">
        {onSkip && <Button size="xs" variant="default" onClick={onSkip}>{t('onboarding.skipStep')}</Button>}
        <Button size="xs" loading={busy} onClick={onNext}>{t('action.continue')}</Button>
      </Group>
    </Group>
  );
}
